// Formatting helpers.

import type { RiverCategory } from '../../functions/_lib/types';

export function formatCfs(cfs: number | null): string {
  if (cfs === null || !Number.isFinite(cfs)) return '—';
  if (cfs >= 10000) return `${(cfs / 1000).toFixed(1)}k`;
  if (cfs >= 1000) return Math.round(cfs).toLocaleString();
  return Math.round(cfs).toString();
}

export function formatPercent(p: number | null): string {
  if (p === null || !Number.isFinite(p)) return '—';
  return `${Math.round(p)}%`;
}

export function formatTimeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'never';
  const sec = Math.floor((Date.now() - t) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}

export function runnableLabel(s: string): string {
  return {
    too_low: 'Too Low',
    runnable: 'Runnable',
    high: 'High',
    very_high: 'Very High',
    flood: 'Flood',
    unknown: '—',
  }[s] ?? '—';
}

export function fishingLabel(s: string): string {
  return {
    too_low: 'Too Low',
    ideal: 'Ideal',
    high_but_fishable: 'High, Fishable',
    too_high: 'Too High',
    unknown: '—',
  }[s] ?? '—';
}

export function runnableBadgeClass(s: string): string {
  return {
    too_low: 'badge-warning',
    runnable: 'badge-success',
    high: 'badge-info',
    very_high: 'badge-warning',
    flood: 'badge-error',
    unknown: 'badge-ghost',
  }[s] ?? 'badge-ghost';
}

export function fishingBadgeClass(s: string): string {
  return {
    too_low: 'badge-warning',
    ideal: 'badge-success',
    high_but_fishable: 'badge-info',
    too_high: 'badge-error',
    unknown: 'badge-ghost',
  }[s] ?? 'badge-ghost';
}

export function statusFor(r: { runnable_status: string; fishing_status: string }, cat: RiverCategory): string {
  return cat === 'fly_fishing' ? r.fishing_status : r.runnable_status;
}

export function statusLabel(s: string, cat: RiverCategory): string {
  return cat === 'fly_fishing' ? fishingLabel(s) : runnableLabel(s);
}

export function statusBadgeClass(s: string, cat: RiverCategory): string {
  return cat === 'fly_fishing' ? fishingBadgeClass(s) : runnableBadgeClass(s);
}

export function percentileBadgeClass(label: string | null): string {
  if (!label) return 'badge-ghost';
  return {
    'Well Below Normal': 'badge-error',
    'Below Normal': 'badge-warning',
    'Normal': 'badge-success',
    'Above Normal': 'badge-info',
    'Well Above Normal': 'badge-primary',
  }[label] ?? 'badge-ghost';
}

/**
 * Short percentile label for badges that have to fit narrow cells (e.g. the
 * drawer's 3-column stat grid where the full "Well Below Normal" wraps).
 * Full label still appears as the badge's title for hover-tooltip.
 */
export function shortPercentileLabel(label: string | null): string {
  if (!label) return '—';
  return {
    'Well Below Normal': 'Very Low',
    'Below Normal': 'Low',
    'Normal': 'Normal',
    'Above Normal': 'High',
    'Well Above Normal': 'Very High',
  }[label] ?? label;
}

export function trendIcon(dir: string): string {
  return { rising: '↗', falling: '↘', steady: '→', unknown: '·' }[dir] ?? '·';
}

export function speciesShortList(species: string[] | undefined): string {
  if (!species || species.length === 0) return '';
  return species.slice(0, 3).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(', ');
}
